import React from 'react';
import { useDeepMemo, Icon } from '@beeline.com/bp-design-system';
import { ObjectColumn } from '../utils/Objects.types';
import { CustomFieldDefinition } from '../../CustomFieldDefinitions/utils/CustomFields.types';
import { useCustomFieldsAndObjectsIntl } from '../../translations/useCustomFieldsAndObjectsIntl';
import { ObjectStatus } from './ObjectStatus';
import { ObjectActions } from './ObjectActions';

interface Props {
  onAdd: (customDefinition: WithOptionalUid<CustomFieldDefinition>) => void;
  onEdit: (customDefinition: WithOptionalUid<CustomFieldDefinition>) => void;
  onSubmitDone: () => void;
  onEditCustomObjectValues: (customObjectDefinitionUid: string, customObjectDefinitionName: string) => void;
}

export const useObjectColumns = ({ onAdd, onEdit, onSubmitDone, onEditCustomObjectValues }: Props): ObjectColumn[] => {
  const intl = useCustomFieldsAndObjectsIntl();
  return useDeepMemo(
    (): ObjectColumn[] => [
      {
        Header: intl.name,
        accessor: 'name',
        defaultWidth: 250,
        CellBeforeContent: ({ row }) => (row.original.external ? <Icon name="lock" /> : ''),
        Cell: ({ value }) => value ?? '-'
      },
      {
        Header: intl.description,
        accessor: 'description',
        Cell: ({ value }) => value ?? '-'
      },
      {
        Header: intl.status,
        accessor: 'status',
        minWidth: 120,
        Cell: ({ value }) => <ObjectStatus status={value} />
      },
      {
        Header: intl.codeLabel,
        accessor: 'code',
        minWidth: 120,
        Cell: ({ value }) => value ?? '-'
      },
      {
        Header: intl.helperTextLabel,
        accessor: 'helperText',
        Cell: ({ value }) => value ?? '-'
      },
      {
        Header: intl.typeLabel,
        accessor: 'dataType',
        minWidth: 120,
        Cell: ({ value }) => value ?? '-'
      },
      {
        Header: intl.valuesLabel,
        accessor: 'values',
        minWidth: 120,
        Cell: ({ value }) => (value?.length ? value.map((v) => v?.label).join(', ') : '-')
      },
      {
        Header: intl.objectLabel,
        minWidth: 140,
        accessor: 'customObjectDefinition',
        Cell: ({ value }) => value?.name ?? '-'
      },
      {
        Header: '',
        accessor: (row) => (
          <ObjectActions
            onSubmitDone={onSubmitDone}
            object={row}
            onEdit={onEdit}
            onAdd={onAdd}
            onEditCustomObjectValues={onEditCustomObjectValues}
          />
        ),
        id: '[menu]',
        sticky: 'right'
      }
    ],
    [
      intl.codeLabel,
      intl.description,
      intl.helperTextLabel,
      intl.name,
      intl.objectLabel,
      intl.status,
      intl.typeLabel,
      intl.valuesLabel,
      onAdd,
      onEdit,
      onSubmitDone,
      onEditCustomObjectValues
    ]
  );
};
